import { FC } from 'react';
import { faFilePdf, faSearch } from '~/assets';
import {
  DatePicker,
  MultipleSelect,
  PaginatedTable,
  Table,
  UserInfoCard,
} from '~/components';
import { Icon } from '~/modules';
import {
  ComplementsPt,
  EventTypesPt,
  fillStringIfEmpty,
  formatHourMinuteSecondDate,
  parseEnumToPt,
  truncateString,
} from '~/utils';
import {
  Actions,
  ActionsRow,
  ActionsTitle,
  Container,
  ContentColumn,
  ExportPDFButton,
  FilterItem,
  FiltersGroup,
  IconSearch,
  SearchIconBox,
  UserInfoColumn,
  Wrapper,
} from './styles';

type Props = {
  clientDetailedInfo: ClientInfoList;
  clientName: string;
  isClientAppAccessBlocked: boolean;
  logs: ListResponse<ClientLog>;
  productOptions: SelectProps[];
  eventTypeOptions: SelectProps[];
  filters: {
    product: SelectProps[];
    eventType: SelectProps[];
    startDate?: CalendarDay;
    endDate?: CalendarDay;
  };
  isLoading: boolean;
  selectedDate: {
    startDate?: CalendarDay;
    endDate?: CalendarDay;
  };
  setSelectedDate: React.Dispatch<
    React.SetStateAction<{
      startDate?: CalendarDay;
      endDate?: CalendarDay;
    }>
  >;
  setFilters: (filters: GetClientLogsFilters) => void;
  navigateToLogDetails(log: ClientLog): void;
  onClearSelectedPeriod(): void;
  onChangePage(page?: number): void;
  onDownloadClientLogs(): void;
  onDatePickerSearch(): void;
};

const Audit: FC<Props> = ({
  clientDetailedInfo,
  clientName,
  isClientAppAccessBlocked,
  logs,
  filters,
  productOptions,
  eventTypeOptions,
  isLoading,
  selectedDate,
  setSelectedDate,
  onClearSelectedPeriod,
  setFilters,
  navigateToLogDetails,
  onChangePage,
  onDownloadClientLogs,
  onDatePickerSearch,
}) => {
  return (
    <Wrapper titlePage="Log de ações">
      <Container>
        <UserInfoColumn>
          <UserInfoCard
            clientName={clientName}
            clientDetailedInfo={clientDetailedInfo}
            isClientAppAccessBlocked={isClientAppAccessBlocked}
          />
        </UserInfoColumn>
        <ContentColumn>
          <Actions>
            <ActionsTitle>Filtros</ActionsTitle>
            <ActionsRow>
              <FiltersGroup>
                <FilterItem>
                  <DatePicker
                    secondary
                    label="Período"
                    selectedStartDate={
                      selectedDate.startDate || filters.startDate
                    }
                    selectedEndDate={selectedDate.endDate || filters.endDate}
                    onClear={onClearSelectedPeriod}
                    onSelectStartDate={(date) =>
                      setSelectedDate({ ...selectedDate, startDate: date })
                    }
                    onSelectEndDate={(date) =>
                      setSelectedDate({ ...selectedDate, endDate: date })
                    }
                    onSearch={onDatePickerSearch}
                  />
                </FilterItem>
                <FilterItem>
                  <MultipleSelect
                    id="select-event"
                    label="Evento"
                    name="event"
                    placeholder="Todos os eventos"
                    options={eventTypeOptions}
                    onChange={({ value }) => {
                      setFilters({
                        ...filters,
                        eventType: value,
                      });
                    }}
                    value={filters.eventType}
                  />
                </FilterItem>
                <FilterItem>
                  <MultipleSelect
                    id="select-product"
                    label="Benefícios"
                    name="product"
                    placeholder="Todos os benefícios"
                    options={productOptions}
                    onChange={({ value }) => {
                      setFilters({
                        ...filters,
                        product: value,
                      });
                    }}
                    value={filters.product}
                  />
                </FilterItem>
              </FiltersGroup>
              <ExportPDFButton
                onPress={onDownloadClientLogs}
                id="export-pdf-button"
              >
                <Icon icon={faFilePdf} fontSize={16} /> Exportar PDF
              </ExportPDFButton>
            </ActionsRow>
          </Actions>
          <PaginatedTable
            columns={[
              'Data e hora',
              'Evento realizado',
              'Complemento',
              'ID do dispositivo',
              '',
            ]}
            list={logs.content}
            totalPages={logs.paging?.totalPages}
            currentPage={logs.paging?.page}
            isLoading={isLoading}
            onChangePage={(page) => onChangePage(page)}
            renderList={(log) => (
              <Table.Tr>
                <Table.Td>
                  {formatHourMinuteSecondDate(new Date(log.createdAt))}
                </Table.Td>
                <Table.Td>
                  {parseEnumToPt(log.eventType, EventTypesPt)}
                </Table.Td>
                <Table.Td>
                  {parseEnumToPt(log.complement, ComplementsPt) ||
                    log.complement}
                </Table.Td>
                <Table.Td>
                  {fillStringIfEmpty(truncateString(log.deviceId, 40))}
                </Table.Td>
                <Table.Td>
                  <SearchIconBox
                    onPress={() => navigateToLogDetails(log)}
                    id="open-log-details"
                  >
                    <IconSearch icon={faSearch} />
                  </SearchIconBox>
                </Table.Td>
              </Table.Tr>
            )}
            titleEmptyData="Nenhum registro encontrado"
          />
        </ContentColumn>
      </Container>
    </Wrapper>
  );
};

export default Audit;
